import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { X, Printer, AlertTriangle, ArrowRight, Info } from "lucide-react";
import { cn } from "@/lib/utils";

// Types
interface StockMovementLineItem {
  id: string;
  stockCode: string;
  description: string;
  location?: string | null;
  quantity: number;
  unit?: string;
  unitPrice: number;
  lineTotal: number;
  notes?: string | null;
  adjustmentType?: "Increase" | "Decrease"; // For Manual Adjustments
}

interface StockMovementDetails {
  id: string;
  reference: string;
  date: Date;
  type: "Sales" | "Purchase" | "Opening Balance" | "Write-off" | "Transfer" | "Manual Adjustment" | "Stock Take Adjustment";
  source: "Manual" | "ERP Sync" | "System Generated";
  userName: string;
  notes?: string | null;
  
  // Type-specific fields
  customerReference?: string | null;
  supplierReference?: string | null;
  writeoffReason?: string | null;
  adjustmentReason?: string | null;
  fromLocation?: string | null;
  toLocation?: string | null;
  openingBalanceDate?: Date | null;
  
  // Transfer-specific
  transferDirection?: "OUT" | "IN";
  relatedMovementReference?: string | null;
  
  // Sync details
  syncDate?: Date | null;
  syncId?: string | null;
  
  // Line items
  lineItems: StockMovementLineItem[];
}

interface StockMovementDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  movementId: string | null;
  onLoadRelatedMovement?: (movementId: string) => void;
}

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

// Format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

// Format date with time
const formatDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Get badge variant for movement type
const getTypeBadgeVariant = (type: StockMovementDetails["type"]) => {
  switch (type) {
    case "Sales":
      return { variant: "default" as const, accent: "cerulean" as const };
    case "Purchase":
      return { variant: "success" as const, accent: "success" as const };
    case "Opening Balance":
      return { variant: "default" as const, accent: "purple" as const };
    case "Write-off":
      return { variant: "destructive" as const, accent: "destructive" as const };
    case "Transfer":
      return { variant: "warning" as const, accent: "warning" as const };
    case "Manual Adjustment":
      return { variant: "outline" as const, accent: undefined };
    case "Stock Take Adjustment":
      return { variant: "default" as const, accent: "teal" as const };
    default:
      return { variant: "outline" as const, accent: undefined };
  }
};

// Get badge variant for source
const getSourceBadgeVariant = (source: StockMovementDetails["source"]) => {
  switch (source) {
    case "Manual":
      return { variant: "outline" as const, accent: undefined };
    case "ERP Sync":
      return { variant: "default" as const, accent: "cerulean" as const };
    case "System Generated":
      return { variant: "default" as const, accent: "teal" as const };
    default:
      return { variant: "outline" as const, accent: undefined };
  }
};

// Mock function to load movement details
const loadMovementDetails = async (movementId: string): Promise<StockMovementDetails | null> => {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 300));
  
  // Mock data - in real implementation, this would fetch from API
  const mockMovement: StockMovementDetails = {
    id: movementId,
    reference: "SO-2024-001",
    date: new Date("2024-01-15"),
    type: "Sales",
    source: "Manual",
    userName: "John Smith",
    notes: "Customer order fulfillment for ABC Corp",
    customerReference: "PO-12345",
    lineItems: [
      {
        id: "1",
        stockCode: "STK001",
        description: "Laptop Dell XPS 15",
        location: "Warehouse A",
        quantity: -5,
        unit: "EA",
        unitPrice: 1200.00,
        lineTotal: -6000.00,
        notes: "Urgent delivery required",
      },
      {
        id: "2",
        stockCode: "STK005",
        description: "Wireless Mouse Logitech",
        location: "Warehouse A",
        quantity: -10,
        unit: "EA",
        unitPrice: 25.50,
        lineTotal: -255.00,
      },
    ],
  };
  
  return mockMovement;
};

export function StockMovementDetailsDrawer({
  open,
  onOpenChange,
  movementId,
  onLoadRelatedMovement,
}: StockMovementDetailsDrawerProps) {
  const [movement, setMovement] = useState<StockMovementDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<{ reference: string; id: string } | null>(null);

  // Load movement details when drawer opens or movementId changes
  useEffect(() => {
    if (open && movementId) {
      setIsLoading(true);
      loadMovementDetails(movementId)
        .then((data) => {
          setMovement(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load movement details:", error);
          setMovement(null);
          setIsLoading(false);
        });
    } else {
      setMovement(null);
      setBreadcrumb(null);
    }
  }, [open, movementId]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onOpenChange]);

  // Calculate summary
  const summary = movement
    ? {
        totalItems: movement.lineItems.length,
        totalQuantity: movement.lineItems.reduce((sum, item) => sum + item.quantity, 0),
        totalValue: movement.lineItems.reduce((sum, item) => sum + item.lineTotal, 0),
      }
    : null;

  // Check if movement is system generated
  const isSystemGenerated = movement && (movement.source === "System Generated" || movement.source === "ERP Sync");

  // Handle print
  const handlePrint = () => {
    console.log("Print clicked for movement:", movement?.reference);
    // TODO: Implement print functionality
  };

  // Handle related movement click
  const handleRelatedMovementClick = (relatedReference: string) => {
    if (movement) {
      setBreadcrumb({ reference: movement.reference, id: movement.id });
      // In real implementation, would need to get the ID from the reference
      onLoadRelatedMovement?.(relatedReference);
    }
  };

  // Handle back to original movement
  const handleBackToBreadcrumb = () => {
    if (breadcrumb) {
      onLoadRelatedMovement?.(breadcrumb.id);
      setBreadcrumb(null);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className={cn(
            "w-full sm:w-[600px] sm:max-w-[600px] p-0 flex flex-col",
            "overflow-hidden"
          )}
          aria-labelledby="movement-details-title"
        >
          {/* Header */}
          <SheetHeader className="bg-muted/50 px-6 py-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <SheetTitle
                  id="movement-details-title"
                  className="text-2xl font-bold truncate"
                >
                  {movement?.reference || "Loading..."}
                </SheetTitle>
                {movement && (
                  <>
                    <Badge {...getTypeBadgeVariant(movement.type)}>
                      {movement.type}
                    </Badge>
                    <Badge {...getSourceBadgeVariant(movement.source)}>
                      {movement.source}
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </SheetHeader>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                  <p className="text-sm text-muted-foreground">Loading movement details...</p>
                </div>
              </div>
            ) : !movement ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto" />
                  <p className="text-lg font-medium">Movement details not found</p>
                  <p className="text-sm text-muted-foreground">
                    The movement you're looking for could not be loaded.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Breadcrumb for Transfer navigation */}
                {breadcrumb && (
                  <div className="flex items-center gap-2 text-sm">
                    <button
                      onClick={handleBackToBreadcrumb}
                      className="text-primary hover:underline"
                    >
                      ← Back to {breadcrumb.reference}
                    </button>
                  </div>
                )}

                {/* System Generated Warning */}
                {isSystemGenerated && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-md">
                    <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <p className="font-medium text-amber-900 dark:text-amber-100">
                        This movement was automatically generated. It cannot be modified.
                      </p>
                      {movement.syncDate && (
                        <div className="text-sm text-amber-800 dark:text-amber-200 space-y-0.5">
                          <p>Sync Date: {formatDateTime(movement.syncDate)}</p>
                          {movement.syncId && <p>Sync ID: {movement.syncId}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Section 1: Movement Information */}
                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Movement Information</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Reference</p>
                      <p className="text-base font-mono">{movement.reference}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Date</p>
                      <p className="text-base">{formatDate(movement.date)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Type</p>
                      <div className="mt-1">
                        <Badge {...getTypeBadgeVariant(movement.type)}>
                          {movement.type}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Source</p>
                      <div className="mt-1">
                        <Badge {...getSourceBadgeVariant(movement.source)}>
                          {movement.source}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">User</p>
                      <p className="text-base">{movement.userName}</p>
                    </div>

                    {/* Conditional fields based on movement type */}
                    {movement.type === "Sales" && movement.customerReference && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Customer Reference</p>
                        <p className="text-base">{movement.customerReference}</p>
                      </div>
                    )}

                    {movement.type === "Purchase" && movement.supplierReference && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Supplier Reference</p>
                        <p className="text-base">{movement.supplierReference}</p>
                      </div>
                    )}

                    {movement.type === "Write-off" && movement.writeoffReason && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Reason</p>
                        <p className="text-base">{movement.writeoffReason}</p>
                      </div>
                    )}

                    {movement.type === "Manual Adjustment" && movement.adjustmentReason && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Reason</p>
                        <p className="text-base">{movement.adjustmentReason}</p>
                      </div>
                    )}

                    {movement.type === "Opening Balance" && movement.openingBalanceDate && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Opening Balance Date</p>
                        <p className="text-base">{formatDate(movement.openingBalanceDate)}</p>
                      </div>
                    )}

                    {movement.type === "Transfer" && movement.fromLocation && movement.toLocation && (
                      <div className="col-span-2">
                        <p className="text-sm font-medium text-muted-foreground mb-2">Transfer Route</p>
                        <div className="flex items-center gap-2 text-base">
                          <span className="font-medium">{movement.fromLocation}</span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{movement.toLocation}</span>
                        </div>
                      </div>
                    )}

                    <div className="col-span-2">
                      <p className="text-sm font-medium text-muted-foreground">Notes</p>
                      <p className="text-base text-muted-foreground">
                        {movement.notes || "No notes provided"}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Transfer-Specific Information */}
                {movement.type === "Transfer" && (
                  <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                            {movement.transferDirection === "OUT"
                              ? `This is the OUTBOUND movement from ${movement.fromLocation}`
                              : `This is the INBOUND movement to ${movement.toLocation}`}
                          </p>
                          {movement.relatedMovementReference && (
                            <div className="mt-2">
                              <p className="text-sm text-blue-800 dark:text-blue-200">
                                Related {movement.transferDirection === "OUT" ? "INBOUND" : "OUTBOUND"} movement:{" "}
                                <button
                                  onClick={() => handleRelatedMovementClick(movement.relatedMovementReference!)}
                                  className="font-medium underline hover:no-underline"
                                >
                                  {movement.relatedMovementReference}
                                </button>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Section 2: Line Items */}
                <Card className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Items</h2>
                  <div className="overflow-x-auto -mx-6 px-6">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-semibold">Stock Code</TableHead>
                          <TableHead className="font-semibold min-w-[200px]">Description</TableHead>
                          {movement.type !== "Transfer" && (
                            <TableHead className="font-semibold">Location</TableHead>
                          )}
                          <TableHead className="font-semibold text-right">Quantity</TableHead>
                          <TableHead className="font-semibold text-right">Unit Price</TableHead>
                          <TableHead className="font-semibold text-right">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {movement.lineItems.map((item, index) => (
                          <TableRow key={item.id} className={index % 2 === 0 ? "bg-muted/30" : ""}>
                            <TableCell className="font-mono font-medium">
                              {item.stockCode}
                            </TableCell>
                            <TableCell>{item.description}</TableCell>
                            {movement.type !== "Transfer" && (
                              <TableCell>{item.location || "-"}</TableCell>
                            )}
                            <TableCell className="text-right font-mono">
                              <span
                                className={cn(
                                  item.quantity > 0
                                    ? "text-green-600 dark:text-green-400"
                                    : item.quantity < 0
                                    ? "text-red-600 dark:text-red-400"
                                    : "text-muted-foreground"
                                )}
                              >
                                {item.quantity > 0 ? "+" : ""}
                                {item.quantity.toLocaleString()}
                                {item.unit && ` ${item.unit}`}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.unitPrice)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              <span
                                className={cn(
                                  item.lineTotal < 0 && "text-red-600 dark:text-red-400"
                                )}
                              >
                                {formatCurrency(item.lineTotal)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>

                {/* Section 3: Movement Summary */}
                {summary && (
                  <Card className="p-6 bg-muted/30">
                    <h2 className="text-lg font-semibold mb-4">Movement Summary</h2>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">Total Items</span>
                        <span className="text-base font-semibold">{summary.totalItems} items</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-muted-foreground">Total Quantity</span>
                        <span
                          className={cn(
                            "text-base font-semibold font-mono",
                            summary.totalQuantity > 0
                              ? "text-green-600 dark:text-green-400"
                              : summary.totalQuantity < 0
                              ? "text-red-600 dark:text-red-400"
                              : ""
                          )}
                        >
                          {summary.totalQuantity > 0 ? "+" : ""}
                          {summary.totalQuantity.toLocaleString()}
                        </span>
                      </div>
                      <div className="pt-3 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-semibold">Total Value</span>
                          <span className="text-2xl font-bold font-mono">
                            {formatCurrency(summary.totalValue)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {movement && (
            <SheetFooter className="bg-muted/50 px-6 py-3 border-t flex-shrink-0">
              <div className="flex items-center justify-between w-full gap-3">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Close
                </Button>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={handlePrint}
                    className="flex items-center gap-2"
                    aria-label="Print movement details"
                  >
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
              </div>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
